import { useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCurrentUser } from '@/hooks/useAuth';
import { companiesApi, defaultFiscalStartMonthForCountry } from '@/lib/companies';
import { currencyForCountry } from '@/i18n/currencyForCountry';
import { Dropdown, DropdownItem } from '@/components/ui/Dropdown';
import { CountryPicker } from '@/components/ui/CountryPicker';
import { CurrencyPicker } from '@/components/ui/CurrencyPicker';

const MONTHS_EN = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const INDUSTRIES = [
  'SaaS', 'Manufacturing', 'Financial services', 'Healthcare', 'Government',
  'Retail', 'Education', 'Media', 'Logistics', 'Other',
];

export function OnboardingPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { data: user, isLoading } = useCurrentUser();

  const initialCountry = (user?.countryCode || 'US').toUpperCase();
  const [name, setName] = useState('');
  const [countryCode, setCountryCode] = useState(initialCountry);
  const [fiscalYearStartMonth, setFiscalYearStartMonth] = useState(defaultFiscalStartMonthForCountry(initialCountry));
  const [reportingCurrency, setReportingCurrency] = useState(currencyForCountry(initialCountry));
  const [industry, setIndustry] = useState<string>('SaaS');
  const [deploymentKind, setDeploymentKind] = useState<'cloud' | 'onprem'>('cloud');
  const [companyUrl, setCompanyUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user?.countryCode) {
      setCountryCode(user.countryCode);
      setFiscalYearStartMonth(defaultFiscalStartMonthForCountry(user.countryCode));
      setReportingCurrency(currencyForCountry(user.countryCode));
    }
  }, [user?.countryCode]);

  const primaryLocale = useMemo(() => (i18n.language || 'en').split('-')[0], [i18n.language]);

  if (isLoading) return <div className="min-h-screen flex items-center justify-center text-ink-500 text-sm">{t('common.loading', { defaultValue: 'Loading…' })}</div>;
  if (!user) return <Navigate to="/?auth=required" replace />;

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!name.trim()) { setError(t('onboarding.name_required', { defaultValue: 'Company name is required' }) as string); return; }
    setSubmitting(true);
    try {
      const { defaultWorkspaceId } = await companiesApi.create({
        name: name.trim(),
        countryCode,
        fiscalYearStartMonth,
        reportingCurrency,
        primaryLocale,
        industry,
        deploymentKind,
        companyUrl: companyUrl.trim() || null,
      });
      navigate(`/app/workspace/${defaultWorkspaceId}`);
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message
        || (err as Error).message;
      setError(typeof msg === 'string' ? msg : (t('onboarding.create_failed', { defaultValue: 'Failed to create company' }) as string));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="min-h-screen bg-ink-50">
      <main className="mx-auto max-w-xl px-6 py-16">
        <div className="flex items-center gap-2.5 font-semibold text-ink-900 mb-8">
          <img src="/apple-touch-icon.png" alt="" width={28} height={28} className="rounded-md" />
          <span className="text-lg">Knowlez</span>
        </div>

        <h1 className="text-3xl font-semibold text-ink-900 tracking-tight">
          {t('onboarding.title', { defaultValue: 'Set up your knowledge plane' })}
        </h1>
        <p className="mt-2 text-ink-600">
          {t('onboarding.subtitle', { defaultValue: 'Under 90 seconds. We auto-detect everything we can.' })}
        </p>

        <form className="mt-8 space-y-5" onSubmit={submit}>
          <Field label={t('onboarding.company_name', { defaultValue: 'Company name' }) as string} required>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('onboarding.company_name_placeholder', { defaultValue: 'Acme K.K.' }) as string}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 outline-none"
            />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label={t('onboarding.country', { defaultValue: 'Country of HQ' }) as string}>
              <CountryPicker
                value={countryCode}
                onChange={(v) => {
                  setCountryCode(v);
                  setFiscalYearStartMonth(defaultFiscalStartMonthForCountry(v));
                  setReportingCurrency(currencyForCountry(v));
                }}
                className="w-full"
              />
            </Field>

            <Field label={t('onboarding.fy_starts', { defaultValue: 'Fiscal year starts' }) as string}>
              <div className="w-full">
                <Dropdown
                  value={String(fiscalYearStartMonth)}
                  onChange={(next) => setFiscalYearStartMonth(Number(next))}
                  items={MONTHS_EN.map((m, i): DropdownItem<string> => ({
                    value: String(i + 1),
                    label: m,
                  }))}
                  ariaLabel={t('onboarding.fy_starts', { defaultValue: 'Fiscal year starts' }) as string}
                  size="md"
                  className="w-full"
                />
              </div>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label={t('onboarding.reporting_currency', { defaultValue: 'Reporting currency' }) as string}>
              <CurrencyPicker
                value={reportingCurrency}
                onChange={setReportingCurrency}
                className="w-full"
              />
            </Field>

            <Field label={t('onboarding.industry', { defaultValue: 'Industry' }) as string}>
              <div className="w-full">
                <Dropdown
                  value={industry}
                  onChange={(next) => {
                    setIndustry(next);
                    // Pre-select on-prem for regulated industries.
                    if (['Financial services', 'Healthcare', 'Government'].includes(next)) {
                      setDeploymentKind('onprem');
                    }
                  }}
                  items={INDUSTRIES.map((c): DropdownItem<string> => ({
                    value: c,
                    label: c,
                  }))}
                  ariaLabel={t('onboarding.industry', { defaultValue: 'Industry' }) as string}
                  size="md"
                  className="w-full"
                />
              </div>
            </Field>
          </div>

          <Field label={t('onboarding.deployment', { defaultValue: 'Deployment' }) as string} hint={t('onboarding.deployment_hint', { defaultValue: 'Cloud is the fast path. On-prem is for air-gapped JP banking / healthcare / government.' }) as string}>
            <div className="grid grid-cols-2 gap-2">
              {(['cloud', 'onprem'] as const).map((k) => (
                <button
                  key={k}
                  type="button"
                  onClick={() => setDeploymentKind(k)}
                  className={`px-3 py-2 rounded-lg text-sm border ${deploymentKind === k ? 'border-indigo-500 bg-indigo-50 text-indigo-900' : 'border-ink-200 bg-white text-ink-700'}`}
                >
                  {k === 'cloud' ? 'Cloud (default)' : 'On-prem'}
                </button>
              ))}
            </div>
          </Field>

          <Field label={t('onboarding.company_url', { defaultValue: 'Company URL (optional)' }) as string} hint={t('onboarding.company_url_hint', { defaultValue: 'We match your OAuth email domain to your company URL for instant verification.' }) as string}>
            <input
              value={companyUrl}
              onChange={(e) => setCompanyUrl(e.target.value)}
              placeholder={t('onboarding.company_url_placeholder', { defaultValue: 'acme.co.jp' }) as string}
              inputMode="url"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2.5 text-sm focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/30 outline-none"
            />
          </Field>

          {error && <div className="rounded-lg bg-red-500/10 border border-red-500/30 text-red-600 px-3 py-2 text-sm">{error}</div>}

          <button
            type="submit"
            disabled={submitting}
            className="w-full inline-flex items-center justify-center px-4 py-3 rounded-lg bg-indigo-600 text-white text-sm font-semibold hover:bg-indigo-700 transition shadow-soft disabled:opacity-60"
          >
            {submitting
              ? t('onboarding.submitting', { defaultValue: 'Creating…' })
              : t('onboarding.submit', { defaultValue: 'Create knowledge plane' })}
          </button>
        </form>
      </main>
    </div>
  );
}

function Field({ label, hint, required, children }: { label: string; hint?: string; required?: boolean; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-ink-700 mb-1.5">
        {label}{required && <span className="text-red-500 ml-0.5">*</span>}
      </span>
      {children}
      {hint && <span className="block text-xs text-ink-500 mt-1">{hint}</span>}
    </label>
  );
}
